// HubSpotChat.jsx
import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
// third party lib
import { useSelector } from 'react-redux';

import FloatingChatBubble from "./FloatingChatBubble";
import { getUserSelector,getAuthResultSelector} from '../../containers/App/selectors';

/**
 * Minimal HubSpot Chat:
 * - Shows a lightweight custom bubble until the first click
 * - On first click: inject loader (EU-first), wait for SDK, widget.load()+open()
 * - After first load: hides custom bubble (per session) and relies on HubSpot's launcher
 * - Exposes window.openHubSpotChat() for opening from anywhere (e.g., footer "Contact us")
 * - Calls widget.refresh() on SPA route changes
 *
 * Props:
 *  - portalId: string (required, e.g. "25941199")
 *  - hublet?: "eu1" | "na1" (default "eu1"); controls loader host
 */
export default function HubSpotChat({ portalId, hublet = "eu1" }) {
  const { pathname, search } = useLocation();
  const lastUrl = useRef("");
  const [showCustomBubble, setShowCustomBubble] = useState(() => !window.__hsInjectedOnce);
  // Email from Redux/Auth0 (may be undefined on initial render)
  const user = useSelector(getUserSelector);
  const authResult = useSelector(getAuthResultSelector);
  const email = user?.email || authResult?.email || null;
  const [isLoading, setIsLoading] = useState(false);

  // Keep the freshest email in a ref to avoid stale closures
  const latestEmailRef = useRef(email);
  useEffect(() => {
    latestEmailRef.current = email || null;
  }, [email]);

  // Track which email we already identified for (prevents duplicate identify)
  const identifiedForRef = useRef(null);

  // Track whether the widget has been loaded at least once
  const widgetLoadedRef = useRef(false);
  
  
  // tiny delay (ms)
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Identify helper (idempotent). Returns true only if identify was performed now.
  // Helper to check if the HubSpot loader/SDK is ready
  const isLoaderReady = () =>
  !!(window.HubSpotConversations &&
     window.HubSpotConversations.widget &&
     typeof window.HubSpotConversations.widget.load === "function");

  // Identify helper (boolean):
  // - Returns true only if a real identify ran while the loader is ready.
  // - If loader isn't ready yet, it queues identify+pageview but returns false.
  const identifyIfNeeded = useCallback(() => {
    const e = latestEmailRef.current;
    if (!e) return false;

    const loaderReady = isLoaderReady();

    // Skip if already identified for this email (only after loader was present)
    if (loaderReady && identifiedForRef.current === e) return false;

    // Queue identify + pageview
    window._hsq = window._hsq || [];
    window._hsq.push(["identify", { email: e }]);
    window._hsq.push(["trackPageView"]); // flush

    if (loaderReady) {
      // Mark as identified only when the loader is available
      identifiedForRef.current = e;
      return true; // a real identify happened now
  }

  // Loader not ready yet → identify is queued, not performed
  return false;
}, []);

  
  // Open handler (first time: load script + SDK; next times: just open)
  const openChat = useCallback(async () => {
    // Helper: inject loader once (EU-first, fallback to global)
    if (isLoading) return; // prevent double-taps
    setIsLoading(true);
    const loadScriptOnce = () =>
      new Promise((resolve, reject) => {
        if (typeof window === "undefined") return reject(new Error("No window"));
        if (document.getElementById("hs-script-loader")) return resolve("existing");

        const base = hublet === "eu1" ? "https://js-eu1.hs-scripts.com" : "https://js.hs-scripts.com";
        const s = document.createElement("script");
        s.id = "hs-script-loader";
        s.async = true;
        s.defer = true;
        s.src = `${base}/${portalId}.js`;
        s.onload = () => resolve("loaded-eu");
        s.onerror = () => {
          // Fallback to global host if EU is blocked/misconfigured
          const g = document.createElement("script");
          g.id = "hs-script-loader";
          g.async = true;
          g.defer = true;
          g.src = `https://js.hs-scripts.com/${portalId}.js`;
          g.onload = () => resolve("loaded-global");
          g.onerror = (e) => reject(new Error("HubSpot loader failed"));
          document.head.appendChild(g);
        };
        document.head.appendChild(s);
      });

    // Helper: wait until SDK is ready (widget.load exists)
    const waitForSdk = (timeoutMs = 30000) =>
      new Promise((resolve, reject) => {
        const start = Date.now();
        // Official onReady hook
        window.hsConversationsOnReady = window.hsConversationsOnReady || [];
        window.hsConversationsOnReady.push(() => resolve());
        // Polling fallback
        const poll = setInterval(() => {
          const ok =
            !!window.HubSpotConversations &&
            !!window.HubSpotConversations.widget &&
            typeof window.HubSpotConversations.widget.load === "function";
          if (ok) {
            clearInterval(poll);
            resolve();
          } else if (Date.now() - start > timeoutMs) {
            clearInterval(poll);
            reject(new Error("HubSpot widget readiness timeout"));
          }
        }, 120);
      });
    if (window.__hsDeferredReset) {
      // Re-issue the reset for safety (in case this is the first HubSpot load)
      window._hsq = window._hsq || [];
      window._hsq.push(['reset']);
      // Clear again just in case
      try {
        const cookies = ['hubspotutk', 'messagesUtk', '__hstc', '__hssc', '__hssrc'];
        const base = window.location.hostname.replace(/^www\./, '');
        cookies.forEach((name) => {
          document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
          document.cookie = `${name}=; domain=${base}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        });
      } catch {}
      // Clear the flag so it doesn't loop
      delete window.__hsDeferredReset;
    }
    try {
      // Ensure lazy behavior: do not auto-init UI before we request it
      window.hsConversationsSettings = {
        ...(window.hsConversationsSettings || {}),
        loadImmediately: false,
      };

      // Give a brief moment for email to arrive (first click race), then identify if possible
      // 1) Ensure loader is present BEFORE anything
      await loadScriptOnce();

      // 2) Wait for SDK readiness (widget.load exists)
      await waitForSdk();

      // 3) Real identify now that loader/SDK are ready
      const didIdentify = identifyIfNeeded(); // true only if we actually identified now

      // 4) Load widget (idempotent) and mark as loaded
      window.HubSpotConversations?.widget?.load?.();
      widgetLoadedRef.current = true;


      // 5) Now open the UI (after refresh has applied identity)
      // Listen for the widget:opened event to hide the custom bubble at the exact right time
     
    
      //window.HubSpotConversations?.widget?.open?.();
      if (didIdentify) {
        await delay(1500);
        window.HubSpotConversations?.widget?.refresh?.();
        await delay(1000);
      }
      window.HubSpotConversations?.widget?.open?.();
      setShowCustomBubble(false);
      window.dispatchEvent(new CustomEvent("hs:loaded"));

      if (!window.__hsInjectedOnce) {
        window.__hsInjectedOnce = true;
        // hs:loaded will be dispatched in the event handler above
      }
    } catch (e) {
      console.log(e);
      // If you see a timeout here, it's typically consent/CSP/adblock blocking the SDK
      // Check cookie banner settings ("Require consent for chat") or CSP for hs domains.
      // console.error("[HS] open failed:", e);
    } finally {
    // If the widget opens, the bubble hides anyway; this ensures we don't leave the button in loading state
     setIsLoading(false);
    }
  }, [hublet, portalId, identifyIfNeeded,isLoading]);

  // Expose a global API so other parts can open the chat (e.g., footer Contact us)
  useEffect(() => {
    window.openHubSpotChat = openChat;
    return () => { if (window.openHubSpotChat === openChat) delete window.openHubSpotChat; };
  }, [openChat]);

    // If email becomes available later, identify and refresh the widget (only if we actually identified now)
  useEffect(() => {
    const didIdentify = identifyIfNeeded();
    // refresh only if the widget already exists (avoid redundant refresh on first-ever load)
   if (didIdentify && widgetLoadedRef.current) {
     window.HubSpotConversations?.widget?.refresh?.();
    }
  }, [email, identifyIfNeeded]);

  // SPA: refresh only when URL actually changes (debounced)
  useEffect(() => {
    const url = pathname + search;
    if (url === lastUrl.current) return;
    lastUrl.current = url;
    const t = setTimeout(() => {
      window.HubSpotConversations?.widget?.refresh?.();
    }, 50);
    return () => clearTimeout(t);
  }, [pathname, search]);

  // Simple lightweight bubble shown until first load; afterwards HubSpot's launcher takes over
  return showCustomBubble ? (
  <FloatingChatBubble openChat={openChat} isLoading={isLoading}/>
  ) : null;
}